import React, { useEffect, useState } from "react";
import { Introduction } from "../components/introduction/Introduction";
import { MissionVision } from "../components/introduction/MissionVision";
import { History } from "../components/introduction/History";
import Banner from "../components/Banner";
import { Trustees } from "../components/introduction/Trustees";
import { useLocation, useNavigate } from "react-router-dom";
import { dynamicFailed } from "../hooks/custom/useToast";
import {
  fetchAboutBot,
  fetchAboutExOfficioMembers,
  fetchAboutHistory,
  fetchAboutIntroduction,
  fetchAboutJoinUs,
  fetchAboutWhoUnepalFor,
  fetchAboutWhyUnepal,
  fetchBotDescription,
  fetchCoreValuesPoints,
  fetchCoreValuesStatement,
  fetchMission,
  fetchVision,
} from "../api/AboutApi";
import {
  IAboutBotData,
  IAboutExOfficioMembersData,
  IAboutHistoryData,
  IAboutIntroductionData,
  IAboutJoinUsData,
  IAboutWhoUnepalForData,
  IAboutWhyUnepalData,
  IBannerData,
  IBotDescriptionData,
  IChancellorMessageData,
  ICoreValuesPointsData,
  ICoreValuesStatementData,
  IMissionVisionData,
} from "../exports/interfaces";
import { useLoader } from "../context/LoaderContext";
import { Message } from "../components/home/Message";
import {
  fetchChancellorMessage,
  fetchViceChancellorMessage,
} from "../api/HomeApi";
import { fetchAboutBanner } from "../api/BannerApi";

const IntroductionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { setLoading } = useLoader();
  const isHashClearedRef = React.useRef(false);

  useEffect(() => {
    if (!location.hash) {
      if (!isHashClearedRef.current) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
      isHashClearedRef.current = false;
    } else {
      const timer = setTimeout(() => {
        isHashClearedRef.current = true;
        navigate(
          { pathname: location.pathname, search: location.search, hash: "" },
          { replace: true },
        );
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash, navigate, location.search]);

  const [aboutIntroduction, setAboutIntroduction] = useState<
    IAboutIntroductionData[]
  >([]);
  const [aboutWhyUnepal, setAboutWhyUnepal] = useState<IAboutWhyUnepalData[]>(
    [],
  );
  const [aboutWhoUnepalFor, setAboutWhoUnepalFor] = useState<
    IAboutWhoUnepalForData[]
  >([]);
  const [aboutJoinUs, setAboutJoinUs] = useState<IAboutJoinUsData[]>([]);
  const [chancellorMessage, setChancellorMessage] = useState<
    IChancellorMessageData[]
  >([]);
  const [viceChancellorMessage, setViceChancellorMessage] = useState<
    IChancellorMessageData[]
  >([]);
  const [visionStatement, setVisionStatement] = useState<IMissionVisionData[]>(
    [],
  );
  const [missionStatement, setMissionStatement] = useState<
    IMissionVisionData[]
  >([]);
  const [coreValuesStatement, setCoreValuesStatement] = useState<
    ICoreValuesStatementData[]
  >([]);
  const [coreValuesPoints, setCoreValuesPoints] = useState<
    ICoreValuesPointsData[]
  >([]);
  const [aboutHistory, setAboutHistory] = useState<IAboutHistoryData[]>([]);
  const [aboutBot, setAboutBot] = useState<IAboutBotData[]>([]);
  const [aboutExOfficioMembers, setAboutExOfficioMembers] = useState<
    IAboutExOfficioMembersData[]
  >([]);
  const [botDescription, setBotDescription] = useState<IBotDescriptionData[]>(
    [],
  );
  const [aboutBanner, setAboutBanner] = useState<IBannerData[]>([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchAboutIntroduction(),
      fetchAboutWhyUnepal(),
      fetchAboutWhoUnepalFor(),
      fetchAboutJoinUs(),
      fetchChancellorMessage(),
      fetchViceChancellorMessage(),
      fetchVision(),
      fetchMission(),
      fetchCoreValuesStatement(),
      fetchCoreValuesPoints(),
      fetchAboutHistory(),
      fetchAboutBot(),
      fetchAboutExOfficioMembers(),
      fetchBotDescription(),
      fetchAboutBanner(),
    ])
      .then(
        ([
          aboutIntroductionData,
          aboutWhyUnepalData,
          aboutWhoUnepalForData,
          aboutJoinUsData,
          chancellorMessageData,
          viceChancellorMessageData,
          visionStatementData,
          missionStatementData,
          coreValuesStatementData,
          coreValuesPointsData,
          aboutHistoryData,
          aboutBotData,
          aboutExOfficioMembersData,
          botDescriptionData,
          aboutBannerData,
        ]) => {
          setAboutIntroduction(aboutIntroductionData);
          setAboutWhyUnepal(aboutWhyUnepalData);
          setAboutWhoUnepalFor(aboutWhoUnepalForData);
          setAboutJoinUs(aboutJoinUsData);
          setChancellorMessage(chancellorMessageData);
          setViceChancellorMessage(viceChancellorMessageData);
          setVisionStatement(visionStatementData);
          setMissionStatement(missionStatementData);
          setCoreValuesStatement(coreValuesStatementData);
          setCoreValuesPoints(coreValuesPointsData);
          setAboutHistory(aboutHistoryData);
          setAboutBot(aboutBotData);
          setAboutExOfficioMembers(aboutExOfficioMembersData);
          setBotDescription(botDescriptionData);
          setAboutBanner(aboutBannerData);
        },
      )
      .catch((error) => {
        dynamicFailed(error.response.data.error.message);
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      });
  }, [setLoading]);

  return (
    <>
      <Banner title="About" bannerImage={aboutBanner?.[0]?.image?.[0]} />
      <Introduction
        aboutIntroduction={aboutIntroduction}
        aboutWhyUnepal={aboutWhyUnepal}
        aboutWhoUnepalFor={aboutWhoUnepalFor}
        aboutJoinUs={aboutJoinUs}
      />
      <Message
        chancellorMessage={chancellorMessage}
        viceChancellorMessage={viceChancellorMessage}
      />
      <MissionVision
        visionStatement={visionStatement}
        missionStatement={missionStatement}
        coreValuesStatement={coreValuesStatement}
        coreValuesPoints={coreValuesPoints}
      />
      <History aboutHistory={aboutHistory} />
      <Trustees
        aboutBot={aboutBot}
        aboutExOfficioMembers={aboutExOfficioMembers}
        botDescription={botDescription}
      />
    </>
  );
};

export default IntroductionPage;
